import { useNavigation, useRoute } from "@react-navigation/native"
import { Button, Screen, Text } from "components"
import { translate } from "i18n"
import React, { useEffect, useState } from "react"
import { Keyboard, Platform, StatusBar, TouchableOpacity, View } from "react-native"
import { style } from "./styles"
import { colors } from "colors"
import { typography } from "theme"
import flash from "config/flash"
import { size, get } from "lodash"
import {
  CodeField,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field"
import { useStores } from "models"
import { useSessionExpired } from "utils/function"
import { useColor } from "app/theme/getThemes"
import Icon from "@expo/vector-icons/Feather"
import { observer } from "mobx-react-lite"

export const VerificationScreen = observer(() => {
  const { companyTheme } = useColor()
  const route = useRoute()
  const { userStore } = useStores()
  const { routeFrom, phoneNumber, pwd, existUser, userData, inactiveUser } = route?.params
  const [isLoading, setIsLoading] = useState(false)

  const navigation = useNavigation<any>()
  const [otpInput, setOtpInput] = useState<string>("")
  const [counter, setCounter] = useState(60)
  const [showSms, setShowSms] = useState(false)
  const [otpType, setOtpType] = useState("whatsapp")

  const ref = useBlurOnFulfill({ value: otpInput, cellCount: 6 })
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value: otpInput,
    setValue: (e) => {
      setOtpInput(e)
    },
  })

  useEffect(() => {
    const interval = setInterval(() => {
      if (counter > 0) {
        setCounter(counter - 1)
      }

      if (counter === 0) {
        setShowSms(true)
        clearInterval(interval)
      }
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  })

  const onResendOTP = async () => {
    await fetch(`${process.env.API_URL}/api/client/otp/${phoneNumber}`, {
      method: "POST",
      body: JSON.stringify({
        company_code: process.env.COMPANY_CODE,
        configure_name: otpType,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((data) => {
        if (data?.ok) {
          flash("success", translate("verificationScreen.resendOTPSuccess"))
          setCounter(60)
        } else {
          flash("error", translate("verificationScreen.resendOTPFailed"))
        }
      })
      .catch(() => {
        flash("error", translate("verificationScreen.resendOTPFailed"))
      })
  }

  const onSwitchMethod = async () => {
    const nextOtpType = otpType === "whatsapp" ? "otp_sms" : "whatsapp"
    setOtpType(nextOtpType)

    console.log("Switching OTP method to:", nextOtpType)

    try {
      const response = await fetch(`${process.env.API_URL}/api/client/otp/${phoneNumber}`, {
        method: "POST",
        body: JSON.stringify({
          company_code: process.env.COMPANY_CODE,
          configure_name: nextOtpType,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (response?.ok) {
        flash("success", translate("verificationScreen.resendOTPSuccess"))
        setCounter(60)
      } else {
        flash("error", translate("verificationScreen.resendOTPFailed"))
      }
    } catch (e) {
      flash("error", translate("verificationScreen.resendOTPFailed"))
    }
  }

  const onSubmit = async () => {
    setIsLoading(true)
    if (size(otpInput) !== 4) {
      flash("error", "Invalid OTP")
      setIsLoading(false)
      return
    }

    try {
      if (inactiveUser) {
        await updateInactiveUser()
      } else {
        const response = await fetch(`${process.env.API_URL}/api/client/verifyotp/${phoneNumber}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            otp_no: otpInput,
            company_code: process.env.COMPANY_CODE,
          }),
        })

        if (response?.ok) {
          flash("success", translate("verificationScreen.verifySuccess"))

          if (routeFrom === "register") {
            await handleLogin()
          } else if (routeFrom === "forgetPin") {
            navigation.navigate("editNewPin", {
              routeFrom: "forgetPin",
              phoneNumber,
              otp: otpInput,
            })
          } else if (routeFrom === "login") {
            navigation.goBack()
          }
        } else {
          const errorMsg = await response?.json()?.catch(() => ({}))
          console.error("API returned error status:", response?.status, errorMsg)

          if (errorMsg?.error === "VERIFY_COUNT_EXCEED") {
            flash("error", translate("verificationScreen.exceedVerifyCount"))
            setCounter(0)
          } else {
            flash("error", errorMsg?.message || translate("verificationScreen.verifyFailed"))
            setOtpInput("")
          }
        }
      }
    } catch (error) {
      console.error("An error occurred during OTP verification fetch:", error)
      flash("error", translate("verificationScreen.verifyFailed"))
    } finally {
      setIsLoading(false)
    }
  }

  const updateInactiveUser = async () => {
    setIsLoading(true)

    try {
      const response = await fetch(`${process.env.API_URL}/api/public/member/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_code: process.env.COMPANY_CODE,
          contact_number: userData?.contact_number,
          member_id: userData?.member_id,
          name: userData?.name,
          email: userData?.email,
          gender: userData?.gender,
          race: userData?.race,
          pwd: userData?.pwd,
          confirm_pwd: userData?.confirmPwd,
          otp_no: parseInt(otpInput),
          status: "active",
          dob: userData?.dob,
          marital_status: userData?.marital_status,
        }),
      })

      if (response?.ok) {
        await handleLogin()
      } else {
        const errorMsg = await response?.json()?.catch(() => ({}))
        console.error("API returned error status:", response?.status, errorMsg)

        if (errorMsg?.error === "VERIFY_COUNT_EXCEED") {
          flash("error", translate("verificationScreen.exceedVerifyCount"))
          setCounter(0)
        } else {
          flash("error", errorMsg?.message || translate("verificationScreen.verifyFailed"))
          setOtpInput("")
        }
      }
    } catch (error) {
      console.error("An error occurred during OTP verification fetch:", error)
      flash("error", translate("verificationScreen.verifyFailed"))
    }
  }
  const handleLogin = async () => {
    setIsLoading(true)

    await fetch(`${process.env.API_URL}/api/client/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contact_number: phoneNumber,
        pwd: userData?.pwd || pwd,
        company_code: process.env.COMPANY_CODE,
      }),
    })
      .then(async (res) => {
        setIsLoading(false)
        if (res?.ok) {
          const responseData = await res?.json()

          console.log("Login ===> ", responseData)

          await fetch(`${process.env.API_URL}/api/client/profile`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `bearer ${responseData?.accessToken}`,
            },
          })
            .then(async (res) => {
              if (res?.ok) {
                const datas = await res?.json()

                userStore.update({
                  id: get(responseData, "member_id", "").toString(),
                  accessToken: get(responseData, "accessToken", ""),
                  fullName: get(responseData, "full_name"),
                  memberCode: "6" + phoneNumber,
                  profileImage: datas?.data.profile_picture,
                  real_member_code: datas?.data.member_code,
                  referral_code: datas?.data.referral_code,
                  flagInfo: userData.flagInfo,
                })

                userStore?.updateIsLoggedIn(true)
                flash("success", translate("loginScreen.loginSuccess"))

                navigation.reset({ index: 0, routes: [{ name: "app" }] })
              } else {
                const errorMsg = await res?.json()
                if (errorMsg?.error === "Token expired" || errorMsg?.error === "Invalid token") {
                  useSessionExpired()
                } else {
                  flash("error", translate("errors.failedToRetreiveMember"))
                }
              }
            })
            .catch((e) => {
              flash("error", translate("errors.failedToRetreiveMember"))
              console.error(e)
            })
        } else {
          const errorMsg = await res?.json()
          console.log(error)
          if (errorMsg?.error === "INVALID_CREDENTIAL") {
            // User found but inccorect password
            flash("error", translate("loginScreen.invalidCredential"))
          } else if (errorMsg?.error === "STATUS_INACTIVE") {
            flash("error", translate("loginScreen.contactUs"))
          } else {
            flash("error", translate("loginScreen.failedToLogin"))
          }
        }
      })
      .catch(() => {
        flash("error", translate("loginScreen.failedToLogin"))
        setIsLoading(false)
      })
  }

  return (
    <Screen onPress={Platform.OS !== "web" && Keyboard.dismiss}>
      <View style={style.container}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: StatusBar.currentHeight + 10,
          }}
        >
          <TouchableOpacity
            style={{
              backgroundColor: companyTheme.first,
              borderRadius: 8,
              paddingHorizontal: 25,
              paddingVertical: 5,
            }}
            onPress={() => navigation.goBack()}
          >
            <Icon name="chevron-left" size={25} color="white" />
          </TouchableOpacity>
        </View>

        <Text style={style.titleText}>{translate("verificationScreen.headerTitle")}</Text>
        <Text>
          {translate("verificationScreen.description")} {phoneNumber}{" "}
          {otpType === "whatsapp" ? "via WhatsApp" : "via SMS"}
        </Text>

        <View style={style.otpContainer}>
          <CodeField
            ref={ref}
            {...props}
            value={otpInput}
            onChangeText={setOtpInput}
            cellCount={4}
            rootStyle={style.rootStyle}
            keyboardType="number-pad"
            renderCell={({ index, symbol, isFocused }) => (
              <Text
                key={index}
                style={[style.cell, { borderColor: isFocused && companyTheme.first }]}
                onLayout={getCellOnLayoutHandler(index)}
              >
                {symbol}
              </Text>
            )}
          />
        </View>

        <Text>{translate("verificationScreen.didntReceiveCode")}</Text>
        <TouchableOpacity
          style={[style.mt10, { alignSelf: "flex-start" }]}
          onPress={() => onResendOTP()}
          disabled={counter > 0}
        >
          <Text
            style={{
              color: counter > 0 ? colors.placeholder : companyTheme.first,
              fontFamily: typography.primary.normal,
            }}
          >
            {translate("verificationScreen.resend")}{" "}
            {counter > 0 && `${translate("verificationScreen.in")} 00:${counter}`}
          </Text>
        </TouchableOpacity>
        {counter === 0 && showSms && (
          <TouchableOpacity
            style={[style.mt10, { alignSelf: "flex-start" }]}
            onPress={onSwitchMethod}
          >
            <Text
              style={{
                color: companyTheme.first,
                fontFamily: typography.primary.normal,
              }}
            >
              {otpType === "whatsapp" ? "Switch to SMS" : "Switch to WhatsApp"}
            </Text>
          </TouchableOpacity>
        )}

        <View style={style.buttonContainer}>
          <Button
            loading={isLoading}
            disabled={size(otpInput) < 4}
            onPress={onSubmit}
            style={[style.button, size(otpInput) < 4 && style.disabledButton]}
            title={translate("common.submit")}
          />
        </View>
      </View>
    </Screen>
  )
})
